import { Divider, Popover } from 'antd'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../../../context/auth-context'
import { FeatureNameEnum } from '../../../graphql/featureToggle/FeatureNameEnum'
import useFeatureToggle from '../../../graphql/featureToggle/useFeatureToggle'
import { ChatbotContainer } from '../../../modules/chatbot/ChatbotContainer'
import { EmployeeSearchComponent } from '../../../modules/employees/EmployeeSearch/EmployeeSearch'
import { EmployeeListPermissions } from '../../../modules/employees/permissions'
import { authenticatedAppRoutes } from '../../../routes/routes'
import { featureEnabled } from '../../../shared/featureToggles/feature-toggle-helper'
import { hasPermission } from '../../../shared/permissions/permission-helper'
import PerButtonCircle from '../../custom_components/ButtonCircle'
import PerFlexRow from '../../custom_components/FlexRow'
import PerIcon from '../../custom_components/Icon'
import { Onboarding } from '../../custom_components/Onboarding/Onboarding'
import { useOnboardingVisibility } from '../../custom_components/Onboarding/Queries'
import { SettingsModal } from '../../custom_components/SettingsModal/SettingsModal'
import { useSettingsModalStore } from '../../custom_components/SettingsModal/SettingsModalStore'
import PerSpacer from '../../custom_components/Spacer'
import PerText from '../../custom_components/Text'
import { useChatbotPermission } from './Queries'

const MenuContent: FC = () => {
  const { user } = useAuth()
  const { t } = useTranslation()

  const iconSize = 20

  return (
    <div className="navigation-menu">
      <a href={`https://tamtamy.reply.com/tamtamy/home.action`} target="_blank" rel="noopener noreferrer">
        <div>
          <PerIcon name="replyTamTamy_fill" size={iconSize} />
          <span>{t('navigation.tamtamy')}</span>
        </div>
      </a>
      <a
        href={`https://replyservicedesk.service-now.com/sp?id=sc_cat_item&sys_id=78963df8db1b58103a2d917bd3961942`}
        target="_blank"
        rel="noopener noreferrer"
      >
        <div>
          <PerIcon name="replyServicedesk_fill" size={iconSize} />
          <span>{t('navigation.serviceDesk')}</span>
        </div>
      </a>
      <a href="https://geco.reply.com" target="_blank" rel="noopener noreferrer">
        <div>
          <PerIcon name="replyGeco_fill" size={iconSize} />
          <span>{t('navigation.geco')}</span>
        </div>
      </a>
      <a href={`${user.skillCenterUrl}`} target="_blank" rel="noopener noreferrer">
        <div>
          <PerIcon name="replySkillCenter_fill" size={iconSize} />
          <span>{t('navigation.skillCenter')}</span>
        </div>
      </a>
    </div>
  )
}

export const Navigation: FC<{ setCollapsed: (collapsed: boolean) => void }> = ({ setCollapsed }) => {
  const [t] = useTranslation()
  const { pathname } = useLocation()
  const { logout, user } = useAuth()
  const userRoles = user.roles || []
  const { data: featureTogglesData, loading, error } = useFeatureToggle()
  const { data: chatbotPermission, loading: chatbotLoading, error: chatbotError } = useChatbotPermission()
  const { data: onboardingVisibility, loading: onboardingLoading, error: onboardingError } = useOnboardingVisibility()

  const globalSearchEnabled =
    featureTogglesData &&
    featureTogglesData.featureToggles &&
    !loading &&
    !error &&
    featureEnabled(FeatureNameEnum.GLOBAL_SEARCH, featureTogglesData.featureToggles)

  const chatBotEnabled =
    featureTogglesData &&
    featureTogglesData.featureToggles &&
    !loading &&
    !error &&
    featureEnabled(FeatureNameEnum.CHATBOT, featureTogglesData.featureToggles)

  const onboardingEnabled =
    featureTogglesData &&
    featureTogglesData.featureToggles &&
    !loading &&
    !error &&
    featureEnabled(FeatureNameEnum.ONBOARDING, featureTogglesData.featureToggles)

  const hasChatbotPermission: boolean = chatbotPermission && chatbotPermission.checkChatbotPermission && !chatbotLoading && !chatbotError

  const hasOnboardingPermission: boolean =
    onboardingVisibility && onboardingVisibility.checkVisibilityOnboarding && !onboardingLoading && !onboardingError

  const toggleModal = useSettingsModalStore((state) => state.toggleModal)

  // When user is in search-results page, we don't want navigation search to be visible
  const searchVisible = pathname !== authenticatedAppRoutes.searchResults()

  return (
    <>
      <div className="navigation">
        <PerFlexRow align="middle" className="logo">
          <img src="../logo_horizontal.svg" alt="logo" />
          {/* Remove after Beta Phase */}
          <>
            <PerSpacer mx="xs">
              <Divider type="vertical" style={{ margin: 0 }} />
            </PerSpacer>
            <PerText fontSize="s" color="secondary">
              Beta
            </PerText>
          </>
        </PerFlexRow>

        {searchVisible && globalSearchEnabled && hasPermission(EmployeeListPermissions.employeeSearch, userRoles) && (
          <EmployeeSearchComponent />
        )}

        <div className="navigation-right">
          {process.env.NODE_ENV === 'development' && (
            <Link to={`/sso/spinitlogout_redirect`} data-e2e-logout-button onClick={logout} className="logout">
              <PerIcon name="logout_stroke" color="error" variant="decoration" size={20} />
              <PerText appearance="flowing-text" color="secondary">
                {t('navigation.logout')}
              </PerText>
            </Link>
          )}

          <Divider type="vertical" className="navigation__divider" />

          <PerButtonCircle
            variant="transparent-variant"
            tooltip={t('navigation.tooltip.settings')}
            icon="cogDouble_fill"
            size="large"
            onClick={() => toggleModal(true)}
          />

          <Divider type="vertical" className="navigation__divider" />

          <Popover placement="bottomRight" content={<MenuContent />} trigger="click">
            <div className="navigation--pointer">
              <PerIcon tooltip={t('navigation.tooltip.otherApps')} name="replyMenu_fill" />
            </div>
          </Popover>

          {chatBotEnabled && hasChatbotPermission && (
            <>
              <Divider type="vertical" className="navigation__divider" />
              <ChatbotContainer setCollapsed={setCollapsed} />
            </>
          )}

          {onboardingEnabled && hasOnboardingPermission && <Onboarding />}

          <PerSpacer ml="m" />
        </div>
      </div>
      <SettingsModal />
    </>
  )
}
